import { useEffect, useMemo, useRef, useState } from 'react';
import { BottomSheet } from '@/components/base/BottomSheet';
import { showToast } from '@/components/base/Toast';
import { confirmModal } from '@/components/base/ConfirmModal';
import { addLog, addMaterial, getFieldsByFarmer, getMaterialsByFarmer, getSeasonsByFarmer } from '@/store';
import { uploadFile, getFileDownloadUrl, uploadFailureMessage } from '@/api/file';
import { MediaCaptureSheet, type CapturedMedia } from '@/components/feature/MediaCaptureSheet';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { ScheduleStepPicker } from '@/pages/farmer/log/components/ScheduleStepPicker';
import type { DiaryAttachment, DiaryIssueSeverity, DiaryMaterial } from '@/types';

interface QuickDiarySheetProps {
  open: boolean;
  onClose: () => void;
  farmerId: string;
}

const RISK_KEYWORDS = ['sâu bệnh', 'vàng lá', 'thiếu nước', 'rầy nâu', 'đạo ôn', 'cháy lá', 'thối rễ', 'héo rũ', 'khô hạn', 'ngập úng'];

export function QuickDiarySheet({ open, onClose, farmerId }: QuickDiarySheetProps) {
  const [fieldId, setFieldId] = useState('');
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [materialId, setMaterialId] = useState('');
  const [newMaterialName, setNewMaterialName] = useState('');
  const [materialQty, setMaterialQty] = useState('');
  const [materialUnit, setMaterialUnit] = useState('kg');
  const [attachments, setAttachments] = useState<DiaryAttachment[]>([]);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [uploadingCount, setUploadingCount] = useState(0);
  const [captureOpen, setCaptureOpen] = useState(false);
  /** fileId -> presigned URL, so what was just attached can be reviewed. */
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});
  const [costMaterials, setCostMaterials] = useState<DiaryMaterial[]>([]);
  const [laborHours, setLaborHours] = useState('');
  const [issueNote, setIssueNote] = useState('');
  const [issueSeverity, setIssueSeverity] = useState<DiaryIssueSeverity | ''>('');
  const [scheduleEventId, setScheduleEventId] = useState('');

  // Not memoized — memoizing an async-cache read on farmerId alone freezes
  // whatever was in cache (often still empty) on this component's first
  // render, never picking up the real data once it arrives. See
  // overview/page.tsx's note for the full explanation.
  const fields = getFieldsByFarmer(farmerId).filter((field) => field.status === 'Đang canh tác');
  const seasons = getSeasonsByFarmer(farmerId);
  const materials = getMaterialsByFarmer(farmerId);

  const linkedSeasonId = useMemo(() => {
    if (!fieldId) return undefined;
    return seasons.find((season) => season.fieldId === fieldId && ['Đang canh tác', 'Chuẩn bị'].includes(season.status))?.id;
  }, [fieldId, seasons]);

  const detectRiskKeywords = (text: string): string[] => {
    const lower = text.toLowerCase();
    return RISK_KEYWORDS.filter((keyword) => lower.includes(keyword.toLowerCase()));
  };

  const handleFilesSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadingCount((n) => n + files.length);
    for (const file of Array.from(files)) {
      try {
        const fileId = await uploadFile(file, 'FARM_LOG_ATTACHMENT', file.type);
        setAttachments((prev) => [...prev, { fileId, type: file.type.startsWith('video') ? 'video' : 'image', caption: file.name }]);
        setPreviewUrls((prev) => ({ ...prev, [fileId]: URL.createObjectURL(file) }));
      } catch {
        showToast(`Tải lên "${file.name}" thất bại`, 'error');
      } finally {
        setUploadingCount((n) => n - 1);
      }
    }
  };

  // Nothing resolved these before, so an attachment was a filename and an icon:
  // a farmer could not check the photo they had just taken and could not play a
  // recording back at all.
  useEffect(() => {
    let cancelled = false;
    attachments.forEach((a) => {
      if (attachmentUrls[a.fileId]) return;
      getFileDownloadUrl(a.fileId)
        .then((url) => {
          if (!cancelled) setAttachmentUrls((prev) => (prev[a.fileId] ? prev : { ...prev, [a.fileId]: url }));
        })
        // Preview only — the attachment is already uploaded and will still save.
        .catch(() => undefined);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  const handleCaptured = async (media: CapturedMedia) => {
    setUploadingCount((n) => n + 1);
    try {
      const fileId = await uploadFile(media.blob, 'FARM_LOG_ATTACHMENT', media.blob.type);
      setAttachments((prev) => [
        ...prev,
        { fileId, type: media.kind, caption: media.kind === 'image' ? 'Ảnh hiện trường.jpg' : 'Video hiện trường.webm' },
      ]);
    } catch (err) {
      showToast(uploadFailureMessage(err, media.kind === 'image' ? 'ảnh' : 'video'), 'error');
    } finally {
      setUploadingCount((n) => n - 1);
    }
  };

  const audio = useAudioRecorder(
    async (blob) => {
      setUploadingCount((n) => n + 1);
      try {
        const fileId = await uploadFile(blob, 'FARM_LOG_ATTACHMENT', blob.type);
        setAttachments((prev) => [...prev, { fileId, type: 'audio', caption: 'Ghi âm hiện trường' }]);
      } catch (err) {
        showToast(uploadFailureMessage(err, 'bản ghi âm'), 'error');
      } finally {
        setUploadingCount((n) => n - 1);
      }
    },
    (message) => showToast(message, 'warning'),
  );

  const removeAttachment = (fileId: string) => setAttachments((prev) => prev.filter((a) => a.fileId !== fileId));

  const handleSave = () => {
    if (!fieldId) {
      showToast('Vui lòng chọn thửa đất', 'warning');
      return;
    }
    if (!title.trim()) {
      showToast('Vui lòng nhập tiêu đề nhật ký', 'warning');
      return;
    }
    if (!detail.trim()) {
      showToast('Vui lòng nhập chi tiết kiểm tra', 'warning');
      return;
    }
    if (uploadingCount > 0) {
      showToast('Đang tải lên tệp đính kèm, vui lòng đợi', 'warning');
      return;
    }

    const selectedMaterial = materials.find((item) => item.id === materialId);
    let materialsNote = '';
    if (selectedMaterial && materialQty) materialsNote = `${selectedMaterial.tenVatTu} ${materialQty}${materialUnit}`;
    if (!selectedMaterial && newMaterialName.trim() && materialQty) {
      materialsNote = `${newMaterialName.trim()} ${materialQty}${materialUnit}`;
      addMaterial({
        farmerId,
        seasonId: linkedSeasonId,
        tenVatTu: newMaterialName.trim(),
        nhomVatTu: 'Tự nhập',
        soLuong: Number(materialQty) || 0,
        tonKho: Number(materialQty) || 0,
        donVi: materialUnit,
        nguonGoc: 'Nông hộ tự mua',
        ngayCap: new Date().toISOString().split('T')[0],
        status: 'Đủ',
        ghiChu: 'Tạo nhanh từ nhật ký',
      });
    }

    addLog({
      farmerId,
      fieldId,
      seasonId: linkedSeasonId,
      scheduleEventId: scheduleEventId || undefined,
      ngay: new Date().toISOString().split('T')[0],
      hoatDong: title.trim(),
      chiTiet: detail.trim(),
      materialsNote: materialsNote || undefined,
      attachments: attachments.length > 0 ? attachments : undefined,
      materials: costMaterials.filter((m) => m.name.trim() && m.quantity > 0).length > 0 ? costMaterials.filter((m) => m.name.trim() && m.quantity > 0) : undefined,
      laborHours: laborHours ? Number(laborHours) : undefined,
      issueNote: issueNote.trim() || undefined,
      issueSeverity: issueSeverity || undefined,
    });

    // Risk/warning detection is now computed server-side (ScheduleService.
    // reconcileLog sets the real FarmLog's warningLevel/warningTags) — the
    // client-side keyword scan + fake notification this used to do is gone.
    const risks = detectRiskKeywords(`${title} ${detail}`);
    showToast(risks.length > 0 ? 'Đã lưu nhật ký (có từ khóa cần chú ý)' : 'Đã lưu nhật ký mới', risks.length > 0 ? 'warning' : 'success');
    resetAndClose();
  };

  const resetAndClose = () => {
    setFieldId('');
    setTitle('');
    setDetail('');
    setMaterialId('');
    setNewMaterialName('');
    setMaterialQty('');
    setMaterialUnit('kg');
    setAttachments([]);
    setCostMaterials([]);
    setLaborHours('');
    setIssueNote('');
    setIssueSeverity('');
    setScheduleEventId('');
    onClose();
  };

  const requestClose = async () => {
    const dirty = title.trim() !== '' || detail.trim() !== '' || attachments.length > 0 || issueNote.trim() !== '';
    if (!dirty) {
      resetAndClose();
      return;
    }
    const ok = await confirmModal({
      title: 'Hủy nhật ký này?',
      message: 'Dữ liệu, ảnh hoặc ghi âm bạn vừa nhập sẽ bị mất nếu thoát ra bây giờ.',
      confirmText: 'Thoát, không lưu',
      cancelText: 'Tiếp tục nhập',
      variant: 'danger',
    });
    if (ok) resetAndClose();
  };

  const addCostMaterialRow = () => setCostMaterials((prev) => [...prev, { name: '', quantity: 0, unit: '' }]);
  const updateCostMaterialRow = (idx: number, patch: Partial<DiaryMaterial>) => {
    setCostMaterials((prev) => prev.map((m, i) => {
      if (i !== idx) return m;
      const next = { ...m, ...patch };
      next.amountVnd = next.unitCostVnd ? next.quantity * next.unitCostVnd : undefined;
      return next;
    }));
  };
  const removeCostMaterialRow = (idx: number) => setCostMaterials((prev) => prev.filter((_, i) => i !== idx));

  return (
    <BottomSheet open={open} onClose={requestClose} title="Tạo nhật ký nhanh">
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-slate-500">Thửa đất <span className="text-red-500">*</span></span>
          <select value={fieldId} onChange={(event) => { setFieldId(event.target.value); setScheduleEventId(''); }} className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm">
            <option value="">Chọn thửa đất</option>
            {fields.map((field) => (
              <option key={field.id} value={field.id}>{field.name} · {field.area}ha</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-slate-500">Tiêu đề <span className="text-red-500">*</span></span>
          <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="VD: Kiểm tra ruộng sau mưa" className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-slate-500">Chi tiết kiểm tra <span className="text-red-500">*</span></span>
          <textarea value={detail} onChange={(event) => setDetail(event.target.value)} rows={4} placeholder="Mô tả tình trạng, công việc đã làm..." className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm" />
        </label>

        <ScheduleStepPicker seasonId={linkedSeasonId} value={scheduleEventId} onChange={setScheduleEventId} />

        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-700">Vật tư đã sử dụng</p>
            <span className="text-[10px] text-slate-400">Nếu có</span>
          </div>
          <div className="grid grid-cols-[1fr_auto_auto] gap-2">
            <select value={materialId} onChange={(event) => setMaterialId(event.target.value)} className="min-w-0 rounded-xl border border-slate-200 px-2 py-2 text-xs">
              <option value="">+ Vật tư mới</option>
              {materials.map((material) => (
                <option key={material.id} value={material.id}>{material.tenVatTu}</option>
              ))}
            </select>
            <input value={materialQty} onChange={(event) => setMaterialQty(event.target.value)} placeholder="SL" className="w-16 rounded-xl border border-slate-200 px-2 py-2 text-xs" />
            <input value={materialUnit} onChange={(event) => setMaterialUnit(event.target.value)} placeholder="kg" className="w-14 rounded-xl border border-slate-200 px-2 py-2 text-xs" />
          </div>
          {!materialId && (
            <input value={newMaterialName} onChange={(event) => setNewMaterialName(event.target.value)} placeholder="Tên vật tư mới" className="mt-2 h-10 w-full rounded-xl border border-slate-200 px-3 text-xs" />
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-700">Chi phí vật tư (theo dòng)</p>
            <button type="button" onClick={addCostMaterialRow} className="text-[10px] font-bold text-agrida-700">+ Thêm dòng</button>
          </div>
          {costMaterials.map((m, idx) => (
            <div key={idx} className="mb-1.5 flex items-center gap-1.5">
              <input value={m.name} onChange={(e) => updateCostMaterialRow(idx, { name: e.target.value })} placeholder="Tên vật tư" className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
              <input type="number" value={m.quantity || ''} onChange={(e) => updateCostMaterialRow(idx, { quantity: Number(e.target.value) })} placeholder="SL" className="w-12 rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
              <input value={m.unit} onChange={(e) => updateCostMaterialRow(idx, { unit: e.target.value })} placeholder="ĐVT" className="w-12 rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
              <input type="number" value={m.unitCostVnd || ''} onChange={(e) => updateCostMaterialRow(idx, { unitCostVnd: Number(e.target.value) })} placeholder="Đơn giá" className="w-16 rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
              <button type="button" onClick={() => removeCostMaterialRow(idx)} className="shrink-0 text-slate-300"><i className="ri-close-line" /></button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-slate-500">Giờ công</span>
            <input type="number" value={laborHours} onChange={(e) => setLaborHours(e.target.value)} placeholder="VD: 4" className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-slate-500">Mức độ vấn đề</span>
            <select value={issueSeverity} onChange={(e) => setIssueSeverity(e.target.value as DiaryIssueSeverity | '')} className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm">
              <option value="">Không có</option>
              <option value="LOW">Nhẹ</option>
              <option value="MEDIUM">Trung bình</option>
              <option value="HIGH">Nghiêm trọng</option>
            </select>
          </label>
        </div>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-slate-500">Vấn đề cụ thể gặp phải</span>
          <textarea value={issueNote} onChange={(e) => setIssueNote(e.target.value)} rows={2} placeholder="VD: Sâu bệnh nhiều hơn dự kiến..." className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm" />
        </label>

        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="mb-2 text-xs font-semibold text-slate-700">Đính kèm</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {/* Live capture. `capture` on an <input> is only a hint a mobile browser
                may honour and does nothing on desktop, so the picker alone was never
                a way to take a photo or record a clip from inside the app. */}
            <button
              type="button"
              onClick={() => setCaptureOpen(true)}
              data-testid="open-capture"
              className="flex min-h-11 items-center justify-center rounded-xl bg-agrida-600 text-xs font-bold text-white"
            >
              <i className="ri-camera-line mr-1" />
              Chụp / Quay
            </button>
            <label className="flex min-h-11 cursor-pointer items-center justify-center rounded-xl border border-slate-200 text-xs font-bold text-slate-700">
              <i className="ri-image-add-line mr-1" />
              Thư viện
              <input type="file" accept="image/*,video/*" multiple className="hidden" data-testid="pick-media" onChange={(e) => { void handleFilesSelected(e.target.files); e.target.value = ''; }} />
            </label>
            <button
              type="button"
              onClick={audio.toggle}
              aria-pressed={audio.recording}
              className={`flex min-h-11 items-center justify-center rounded-xl border text-xs font-bold ${audio.recording ? 'border-rose-500 bg-rose-500 text-white' : 'border-slate-200 text-slate-700'}`}
            >
              <i className={`mr-1 ${audio.recording ? 'ri-stop-circle-fill' : 'ri-mic-line'}`} />
              {audio.state === 'starting'
                ? 'Đang mở micro...'
                : audio.recording
                  ? `Dừng ghi ${Math.floor(audio.elapsed / 60)}:${String(audio.elapsed % 60).padStart(2, '0')}`
                  : 'Ghi âm'}
            </button>
            <div className="flex min-h-11 items-center justify-center rounded-xl border border-dashed border-slate-200 text-[10px] text-slate-400">
              {uploadingCount > 0 ? `Đang tải ${uploadingCount}...` : `${attachments.length} tệp`}
            </div>
          </div>
          {attachments.length > 0 && (
            <ul className="mt-2 space-y-2">
              {attachments.map((a) => {
                const url = attachmentUrls[a.fileId];
                return (
                  <li key={a.fileId} className="rounded-lg bg-slate-50 p-2" data-testid={`attachment-${a.type}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="min-w-0 truncate text-[11px] text-slate-600">
                        <i className={`mr-1 ${a.type === 'audio' ? 'ri-mic-line' : a.type === 'video' ? 'ri-video-line' : 'ri-image-line'}`} />
                        {a.caption ?? a.fileId}
                      </span>
                      <button type="button" onClick={() => removeAttachment(a.fileId)} aria-label="Xoá đính kèm" className="shrink-0 text-slate-300 hover:text-red-500">
                        <i className="ri-close-line" />
                      </button>
                    </div>
                    {!url ? (
                      <p className="mt-1 text-[10px] text-slate-400">Đang tải bản xem trước...</p>
                    ) : a.type === 'audio' ? (
                      <audio src={url} controls preload="metadata" data-testid="attachment-audio-player" className="mt-1.5 w-full" />
                    ) : a.type === 'video' ? (
                      <video src={url} controls preload="metadata" data-testid="attachment-video-player" className="mt-1.5 max-h-56 w-full rounded-lg bg-black" />
                    ) : (
                      <a href={url} target="_blank" rel="noopener noreferrer" title="Mở ảnh kích thước đầy đủ">
                        <img src={url} alt={a.caption ?? 'ảnh đính kèm'} data-testid="attachment-image-preview" className="mt-1.5 max-h-56 w-full rounded-lg bg-black object-contain" />
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <MediaCaptureSheet open={captureOpen} onClose={() => setCaptureOpen(false)} onCaptured={handleCaptured} />
        </div>

        <button type="button" onClick={handleSave} className="min-h-12 w-full rounded-xl bg-agrida-600 text-sm font-bold text-white">
          Lưu nhật ký
        </button>
      </div>
    </BottomSheet>
  );
}
